import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Settings, User, LogOut, Home } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useBranding } from "../../contexts/BrandingContext";
import { useProject } from "../../contexts/ProjectContext";
import { useReadOnly } from "../../contexts/ReadOnlyContext";
import { useRouteContext } from "../../contexts/RouteContext";
import { usePreview } from "../../contexts/PreviewContext";
import { APP_NAV_CONFIGS } from "../../data/appNavConfig";
import AppSelector from "./AppSelector";
import ImpersonatePicker from "./ImpersonatePicker";
import ContextSelector from "./ContextSelector";
import InlineSearch from "./InlineSearch";
import SectionTabsScroller from "./SectionTabsScroller";
import { ShareProjectButton } from "../ShareProjectDialog";

// ── Props ───────────────────────────────────────────────────────

export interface HeaderRow1Props {
  currentApp: import("../../data/appShellTypes").SubApp | null;
  entitledApps: import("../../data/appShellTypes").SubApp[];
  onSwitchApp: (slug: string) => void;
}

// ── HeaderRow1 ──────────────────────────────────────────────────

export default function HeaderRow1({
  currentApp,
  entitledApps,
  onSwitchApp,
}: HeaderRow1Props) {
  const { user } = useAuth();
  const { brand } = useBranding();
  const { currentProject, projects } = useProject();
  const readOnly = useReadOnly();
  const { appPrefix } = useRouteContext();
  const { isLocalPreview, workspacePath } = usePreview();
  const location = useLocation();
  const navigate = useNavigate();

  const navConfig = currentApp ? APP_NAV_CONFIGS[currentApp.slug] : null;
  const searchEnabled = navConfig?.searchEnabled ?? false;
  const appTitle = navConfig?.title ?? null;

  const isInternal = user?.userType === "internal";
  const hasPartnerAdminRole = user?.partnerMemberships?.some((pm: any) => pm.role === "admin") ?? false;
  const hasClientAdminRole = user?.clientMemberships?.some((cm: any) => cm.role === "admin") ?? false;
  // Internal users only see Settings if they admin/edit at least one project.
  // Viewer-only internal users (and non-internal users without partner/client
  // admin) get a 403 from /api/admin/projects anyway — hiding the gear keeps
  // them from clicking into a dead-end page.
  const hasManageableProject = projects.some((p) => p.role === "admin" || p.role === "editor");
  const showSettings = !readOnly && (
    user?.isSuperAdmin
    || hasPartnerAdminRole
    || hasClientAdminRole
    || (isInternal && hasManageableProject)
  );
  const isSettings = location.pathname.startsWith("/studio/settings");
  const contextType = navConfig?.contextType ?? "none";

  const settingsHref = "/studio/settings";

  // Set tab title for local preview to identify workspace
  useEffect(() => {
    if (isLocalPreview && workspacePath) {
      const wsName = workspacePath.split("/").pop() || "local";
      const projectName = currentProject?.name || currentProject?.slug || "";
      document.title = `${wsName}${projectName ? ` · ${projectName}` : ""} — Cortex Local`;
    }
  }, [isLocalPreview, workspacePath, currentProject]);

  return (
    <>
      {isLocalPreview && workspacePath && <LocalPreviewBanner path={workspacePath} />}
      <header className="shrink-0 border-b border-slate-200 bg-slate-100 flex flex-col">
        {/*
          5-column grid — single row header.
          Columns: brand | project(240px) | branch(240px) | actions+search(1fr) | icons
          Status badges shown inside gear dropdown, not as a separate row.
        */}
        <div
          className="grid items-center px-4 py-2"
          style={{
            gridTemplateColumns: "auto 240px auto 1fr auto",
            columnGap: "24px",
          }}
        >
          {/* ═══ ROW 1 ═══ */}

          {/* Col 1: Logo + brand */}
          <div className="h-14 flex items-center gap-3">
            <img src={brand.logoUrl} alt="" className="size-8 rounded-full object-cover shrink-0" />
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-[18px] font-semibold bg-gradient-to-r from-indigo-900 to-indigo-600 bg-clip-text text-transparent tracking-[0.04em] select-none whitespace-nowrap">
                {brand.displayName || "Cortex"}
              </span>
              {appTitle && appTitle !== brand.displayName && (
                <span className="text-[14px] font-medium bg-gradient-to-r from-indigo-400 to-indigo-300 bg-clip-text text-transparent select-none tracking-wide whitespace-nowrap">
                  {appTitle.replace(brand.displayName || "", "").trim()}
                </span>
              )}
            </div>
          </div>

          {/* Col 2: Project selector */}
          <div className="h-14 flex items-center">
            {contextType !== "none" ? (
              <div className="w-[240px]">
                <ContextSelector contextType={contextType} />
              </div>
            ) : <div />}
          </div>

          {/* Col 3: Branch selector slot */}
          <div className="h-14 flex items-center">
            {currentApp && !readOnly ? (
              <div id="header-branch-slot" className="flex items-center" />
            ) : <div />}
          </div>

          {/* Col 4: Action buttons + Search */}
          <div className="h-14 flex items-center gap-3 min-w-0">
            {currentApp && (
              <>
                <div id="header-staging-slot" className="flex items-center shrink-0" />
                <div id="header-peek-slot" className="flex items-center shrink-0" />
                <div id="header-fetch-slot" className="flex items-center shrink-0" />
                <div id="header-manage-slot" className="flex items-center shrink-0" />
              </>
            )}
            <div className="flex-1" />
            {searchEnabled && (
              <InlineSearch appPrefix={appPrefix} readOnly={readOnly} maxWidth="max-w-[400px]" />
            )}
          </div>

          {/* Col 5: Right icons */}
          <div className="h-14 flex items-center gap-0.5">
            {/* Not in Settings: no project is on screen there (it has its own
                Members and Share links tabs), and the General tab would go
                stale under an audience change made from the dialog. */}
            {!isLocalPreview && !isSettings && currentApp && (
              <div className="mr-1.5 flex items-center empty:hidden"><ShareProjectButton labelFrom="2xl" /></div>
            )}
            {!readOnly && (
              <button
                onClick={() => {
                  if (currentProject) {
                    navigate(`${appPrefix}/projects/${currentProject.slug}/docs`);
                  } else {
                    navigate(appPrefix);
                  }
                }}
                className="p-1.5 rounded-md transition-colors text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                aria-label="Home"
              >
                <Home className="size-5" />
              </button>
            )}
            {!isLocalPreview && entitledApps.length > 1 && (
              <AppSelector
                apps={entitledApps}
                currentApp={currentApp}
                onSwitch={onSwitchApp}
              />
            )}
            {!isLocalPreview && showSettings && (
              <NavLink
                to={settingsHref}
                className={() =>
                  `p-1.5 rounded-md transition-colors ${isSettings ? "text-indigo-600 bg-indigo-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"}`
                }
                aria-label="Settings"
              >
                <Settings className="size-5" />
              </NavLink>
            )}
            <HeaderUserMenu />
          </div>

        </div>

        {/* Row 2: section tabs */}
        {currentApp && <SectionTabsScroller />}
      </header>
    </>
  );
}


// ── LocalPreviewBanner ───────────────────────────────────────────

function LocalPreviewBanner({ path }: { path: string }) {
  const segments = path.split("/").filter(Boolean);
  const homeIdx = segments[0] === "Users" ? 2 : 0;

  return (
    <div
      className="h-8 flex items-center px-5 gap-3 overflow-hidden select-none"
      style={{ background: "#0c1222" }}
    >
      <span className="flex items-center gap-2 shrink-0">
        <svg className="size-3.5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 15l3-3-3-3" /><line x1="13" y1="15" x2="17" y2="15" /></svg>
        <span className="text-[11px] font-medium text-emerald-400/80 tracking-wide">Preview</span>
      </span>

      <div className="flex items-center min-w-0 overflow-hidden">
        {segments.map((seg, i) => {
          const isHome = i < homeIdx;
          const isLast = i === segments.length - 1;
          const isMiddle = !isHome && !isLast;
          return (
            <span key={i} className="flex items-center shrink-0">
              <span className="text-[12px] text-slate-600 mx-1.5">/</span>
              <span
                className={
                  isLast
                    ? "text-[12px] font-bold text-emerald-300"
                    : isMiddle
                      ? "text-[12px] text-slate-300"
                      : "text-[12px] text-slate-500"
                }
              >
                {seg}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}


// ── HeaderUserMenu ──────────────────────────────────────────────

function HeaderUserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded-md hover:bg-slate-50 transition-colors" aria-label="User menu">
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} className="size-6 rounded-full" alt="" />
        ) : (
          <div className="size-6 rounded-full bg-slate-200 flex items-center justify-center">
            <User className="size-3.5 text-slate-500" />
          </div>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 w-72">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[12px] font-medium text-slate-700 truncate">{user?.name || user?.email}</div>
            {user?.name && <div className="text-[11px] text-slate-400 truncate">{user.email}</div>}
          </div>
          <ImpersonatePicker />
          <button onClick={() => { logout(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-red-600 hover:bg-red-50 transition-colors">
            <LogOut className="size-3" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
